import { Country } from '@/constants/Country';
import type { RouteName } from '@/constants/RouteName';

const noZeroRegex = /^[.]\d*/;
const numberInput = /[^-.0-9]+/;
const alphaNumericInput = /^[a-zA-Z0-9]+$/;

/** Complete scientific notation (e.g. `1e-8`, `1.5E+2`, `.5e-2`). */
const scientificNotationPattern = /^([+-]?)(?:(\d+)\.(\d*)|(\d+)|\.(\d+))[eE]([+-]?\d+)$/;

/**
 * Expands a scientific-notation string into plain decimal form without using
 * floating-point stringification
 */
function expandScientificNotationToDecimal(value: string): string | null {
  // [optional sign, integer, decimal, integer only, decimal only, exponent]
  const match = value.trim().match(scientificNotationPattern);

  if (!match) {
    return null;
  }

  const sign = match[1] === '-' ? '-' : '';
  let intPart: string;
  let fracPart: string;

  if (match[2] !== undefined) {
    intPart = match[2];
    fracPart = match[3] ?? '';
  } else if (match[4] !== undefined) {
    intPart = match[4];
    fracPart = '';
  } else {
    intPart = '0';
    fracPart = match[5] ?? '';
  }

  const exponent = parseInt(match[6], 10);
  const digits = intPart + fracPart;
  const decimalPosition = intPart.length + exponent;

  if (decimalPosition <= 0) {
    return `${sign}0.${'0'.repeat(-decimalPosition)}${digits}`;
  }

  if (decimalPosition >= digits.length) {
    return `${sign}${digits}${'0'.repeat(decimalPosition - digits.length)}`;
  }

  return `${sign}${digits.slice(0, decimalPosition)}.${digits.slice(decimalPosition)}`;
}

type GetSignedNumberOptions =
  | number
  | {
      numDecimalPoints?: number;
      includeSpace?: boolean;
    };

/**
 * @deprecated
 */
export default class CommonUtils {
  public static formatNumberIntoCurrency(
    country: Country,
    num: number,
    minimumFractionDigits: number,
    maximumFractionDigits: number,
  ): string {
    switch (country) {
      case Country.US:
        return new Intl.NumberFormat('en-us', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits,
          maximumFractionDigits,
        }).format(num);
      default:
        return num.toString();
    }
  }

  public static formatNumberInput(value: string | null): string | null {
    let newVal = value;
    // Add a zero, in the case of '.xx'
    if (newVal && newVal.match(noZeroRegex) && newVal.length > 1) {
      newVal = `0${newVal}`;
    }

    // Handle scientific notation
    // Example:
    // 1e-8 -> 0.00000001
    // 1.23e+2 -> 123
    // 1.23e-2 -> 0.0123
    if (newVal && /[eE]/.test(newVal)) {
      const expanded = expandScientificNotationToDecimal(newVal);
      if (expanded) {
        newVal = expanded;
      }
    }

    // Strip thousands separators before removing other non-numeric characters
    if (newVal) {
      newVal = newVal.replace(/,/g, '');
    }

    // Remove all characters that are not a number, '-' or '.'
    if (newVal && newVal.match(numberInput)) {
      newVal = newVal.replace(numberInput, '');
    }

    // Remove the last character that made the number not finite
    // Or trim the infinite number to 10 characters
    if (newVal && !Number.isFinite(Number(newVal)) && newVal.length > 1) {
      newVal = newVal.slice(0, -1);
      newVal = newVal.substring(0, 10);
    }

    return newVal;
  }

  public static getExcelColumnHeaderByColumnIndexPosition(columnIndex: number): string {
    const columnHeaderCodeList = [];
    while (columnIndex > 0) {
      const rem = columnIndex % 26;
      if (rem === 0) {
        columnHeaderCodeList.push('Z');
        columnIndex = Math.floor(columnIndex / 26) - 1;
      } else {
        columnHeaderCodeList.push(String.fromCharCode(rem - 1 + 'A'.charCodeAt(0)));
        columnIndex = Math.floor(columnIndex / 26);
      }
    }
    return `COLUMN ${columnHeaderCodeList.reverse().join('')}`;
  }

  /**
   * Format file name of the pdf or excel file to include the tool, slug, and date
   * @param res API output to get the slug and date
   * @param tool page used to create file
   */
  public static getSanitizedFileName(tool: RouteName, indexName: string, date?: string): string {
    let retval = `${tool} - ${indexName}`;
    if (date) retval += ` as of ${date}`;

    return retval.replace(/['`~!@#$%^&*()_|+=?;:'",.<>{}[]\\\/]/gi, '');
  }

  /**
   * Formats a number into a string with a '+' sign if positive, '-' if negative,
   * and in fixed-point notation when specified
   *
   * @param value number to format
   * @param toFixed number of fixed-point notation to round to
   * @returns
   */
  public static getSignedNumber(value: number, options?: number | GetSignedNumberOptions): string {
    /**
     * For the hotfix, preserve the original logic.
     * In an upcoming PR, the instances using getSignedNumber will be updated to use the new
     * `GetSignedNumberOptions` type
     */
    if (typeof options === 'number') {
      return value > 0
        ? `+${value.toLocaleString(undefined, {
            minimumFractionDigits: options,
            maximumFractionDigits: options,
          })}`
        : value.toLocaleString(undefined, {
            minimumFractionDigits: options,
            maximumFractionDigits: options,
          });
    }

    if (options) {
      const numDecimalPoints = options.numDecimalPoints;
      const shouldIncludeSpace = options?.includeSpace ?? false;

      const unsignedPositiveOrNegativeNumber = value.toLocaleString(undefined, {
        minimumFractionDigits: numDecimalPoints,
        maximumFractionDigits: numDecimalPoints,
      });
      const spaceIfNecessary = shouldIncludeSpace ? ' ' : '';

      return value > 0
        ? `+${spaceIfNecessary}${unsignedPositiveOrNegativeNumber}`
        : `${unsignedPositiveOrNegativeNumber.slice(0, 1)}${spaceIfNecessary}${unsignedPositiveOrNegativeNumber.slice(
            1,
          )}`;
    }

    return value > 0 ? `+${value.toLocaleString()}` : value.toLocaleString();
  }

  public static isAlphaNumeric(value: string): boolean {
    return value.length > 0 && alphaNumericInput.test(value);
  }
}
